import React from 'react';
import classNames from 'classnames';
import { Observe } from 'rewire-core';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import EditIcon from '@material-ui/icons/EditOutlined';
import ErrorIcon from '@material-ui/icons/ErrorOutline';
import { EditTimecardModel, IDetailFields, IDetailRow } from './EditTimecardModel';
import { makeStyles, Theme } from '../../utility/styles';
import { editor, IEditorField, IActionMenuItem } from 'rewire-ui';
import { useKeyboardNavigation } from './KeyboardNavigation';
import AddReasonDialog from '../../schedule/AddReasonDialog';
import { useWatch } from '../../settings/SettingsModel';
import UdfEditor from '../../settings/UdfEditor';
import OptionsActionMenu from '../../ui/OptionsActionMenu';
import { roundTo } from '../../utility/DataConverters';

interface IDetailRowProps {
  detailRow: IDetailRow;
  row: number;
  model: EditTimecardModel;
}

interface IDetailCellProps {
  classes: any;
  detailRow: IDetailRow;
  detailName: keyof IDetailFields;
  model: EditTimecardModel;
}

interface IHeaderRowProps {
  model: EditTimecardModel;
}

const useDetailRowStyles = makeStyles((theme: Theme) => ({
  root: {
    '& fieldset': {
      borderColor: 'transparent',
    },
    '& .Mui-disabled fieldset': {
      borderColor: 'transparent !important',
    },
    '&.break .reason': {
      color: '#b9b9b9',
    },
    '&.worked .overtimeReason': {
      color: '#14809d',
    },
    '&.absence .reason': {
      color: '#14809d',
    },
    '&.premium .reason': {
      color: '#9e159e',
    },
    '&.empty': {
      color: '#669639 !important',
      fontStyle: 'italic',
    },
    '&.editing input': {
      fontWeight: 'bold !important',
    },
    '& .detail-cell-editor': {
      width: '100%',
    },
  },
  row: {
    width: 4,
    display: 'inline-block',
    height: 32,
    marginTop: 2,
    backgroundColor: '#c8d9ea',
  },
  rowButton: {
    width: 28,
    height: 28,
    marginTop: '4px',
    marginLeft: '4px',
  },
  deleteActivityButton: {
    '&:hover': {
      color: theme.palette.error.main,
    },
  },
  editButton: {
    color: 'darkorange',
    '&:hover': {
      color: '#db7900',
    },
  },
  editing: {
    backgroundColor: '#14809d',
  },
  locked: {
    backgroundColor: '#b9b9b9',
  },
  hidden: {
    display: 'none',
  },
  heading: {
    '& > span': { marginLeft: 14 },
    textAlign: 'left',
    margin: '0 2px',
    borderBottom: '1px solid #ddd',
  },
  wrapper: {
    display: 'inline-flex',
    flex: 1,
  },
  detail: {
    padding: '4px 2px',
    borderBottom: '1px solid #eee',
    '& p': { margin: '0', position: 'absolute', right: 1, top: 8 },
  },
  header: {
    fontWeight: 'bold',
    lineHeight: '24px',
  },
}));

function setFocus(
  model: EditTimecardModel,
  focusInfo: { rowIdx: number; column: keyof IDetailFields; field: IEditorField } | undefined,
) {
  if (!focusInfo) return;
  let e: HTMLElement | null = null;
  const row = model.detailRows[focusInfo.rowIdx];
  if (row) {
    e = document.getElementById(row.data[focusInfo.column]?.name ?? '');
  }
  if (!e && focusInfo.field) e = document.getElementById(focusInfo.field.name);
  e?.querySelector('input')?.focus();
}

function getFocus(model: EditTimecardModel): { rowIdx: number; column: keyof IDetailFields; field: IEditorField } | undefined {
  let element = document.activeElement;
  const row = model.currentRow;
  if (!row) return;
  const rowIdx = model.detailRows.findIndex((r) => r._id === row._id);
  while (element) {
    const item = element.id
      ? Object.entries(row?.data).find(([_key, value]: [string, IEditorField]) => value.name === element?.id)
      : null;
    if (item) {
      return { rowIdx, column: item[0] as keyof IDetailFields, field: item[1] as IEditorField };
    }
    element = element.parentElement;
  }
}

const DetailRow = (props: IDetailRowProps) => {
  const classes = useDetailRowStyles();
  const detailRow = props.detailRow;
  const model = props.model;
  const handleFocus = (_evt: React.FocusEvent<any>) => {
    props.model.currentRow = detailRow;
  };

  const performEdit = async () => {
    try {
      const focus = getFocus(props.model);
      if (document.activeElement) {
        const f = document.activeElement as HTMLInputElement;
        f.blur();
        f.focus();
      }
      const success = await props.model.edit();
      requestAnimationFrame(() => setFocus(props.model, focus));
      return success;
    } catch (error) {
      console.error('Error performing edit:', error);
      return false;
    }
  };

  const handleOnKeyDown = async (evt: React.KeyboardEvent<any>) => {
    let key = evt.key;
    if (key === 'Shift' || key === 'Control' || key === 'Alt') return;
    if (evt.shiftKey) {
      key = `Shift+${key}`;
    }
    if (evt.ctrlKey) {
      key = `Ctrl+${key}`;
    }
    if (evt.altKey) {
      key = `Alt+${key}`;
    }

    switch (key) {
      case 'Enter': {
        await performEdit();
        evt.preventDefault();
        return;
      }

      case 'Escape':
        props.model.undoRow();
        evt.preventDefault();
        return;

      case 'Ctrl+S':
      case 'Ctrl+s': {
        // eslint-disable-next-line no-case-declarations
        const success = await performEdit();
        if (success) {
          props.model.emit('save');
        }
        evt.preventDefault();
        return;
      }
    }
  };
  const category = (!detailRow.empty && detailRow.data.reason.value?.category) ?? '';
  return (
    <Observe
      render={() => (
        <tr
          className={classNames(classes.root, 'tabrow', { empty: detailRow.empty }, category)}
          onKeyDown={handleOnKeyDown}
          onFocus={handleFocus}
        >
          <Observe>
            {() => {
              const locked = detailRow.data.reason?.disabled?.(detailRow.data.reason) ?? false;
              return <td className={classNames(classes.row, { [classes.locked]: locked, [classes.editing]: detailRow.editing })} />;
            }}
          </Observe>
          <Observe>
            {() => (
              <>
                {detailRow.hasChanges ? (
                  <td>
                    <IconButton className={classNames(classes.rowButton, classes.editButton)} onClick={() => model.edit()}>
                      <EditIcon />
                    </IconButton>
                  </td>
                ) : !detailRow.empty ? (
                  <td>
                    <IconButton
                      className={classNames(classes.rowButton, classes.deleteActivityButton)}
                      disabled={detailRow.data.reason?.disabled?.(detailRow.data.reason) ?? false}
                      onClick={() => model.remove(detailRow)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </td>
                ) : (
                  // biome-ignore lint/a11y/noNoninteractiveTabindex: <explanation>
                  <td tabIndex={0} />
                )}
              </>
            )}
          </Observe>
          {Object.keys(detailRow.data).map((detailName: keyof IDetailFields) => {
            return (
              <DetailCell
                key={detailRow.data[detailName]?.name}
                model={model}
                detailRow={detailRow}
                detailName={detailName}
                classes={classes}
              />
            );
          })}
        </tr>
      )}
    />
  );
};

const DetailCell = (props: IDetailCellProps) => {
  const { model, detailRow, detailName, classes } = props;
  const detailField: IEditorField = detailRow.data[detailName as keyof IDetailFields]!;
  const fieldInfo = model.getFieldInfo(detailName);

  return (
    <td
      key={detailField.name}
      id={detailField.name}
      className={classNames(classes.detail, `span${fieldInfo.columns}`, { [classes.hidden]: !detailField.visible })}
    >
      <detailField.Editor className={`${detailName} detail-cell-editor`} />
    </td>
  );
};

const HeaderRow = ({ model }: IHeaderRowProps) => {
  const classes = useDetailRowStyles();
  return (
    <Observe>
      {() => {
        const row = model.detailRows[0];
        if (!row) return null;

        return (
          <>
            <colgroup>
              <col style={{ width: '4px' }} />
              <col style={{ width: '32px' }} />
              {Object.keys(row.data).map((detailName: keyof IDetailFields) => {
                const detailField = row.data[detailName];
                const fieldInfo = model.getFieldInfo(detailName);
                return (
                  <col
                    key={`cg${detailField?.name}`}
                    className={classNames(`span${fieldInfo.columns}`, classes.heading, { [classes.hidden]: !detailField?.visible })}
                  />
                );
              })}
            </colgroup>
            <thead className={classes.header}>
              <tr>
                <th />
                <th />
                {Object.keys(row.data).map((detailName: keyof IDetailFields) => {
                  const detailField = row.data[detailName];
                  const fieldInfo = model.getFieldInfo(detailName);
                  return (
                    <th
                      key={`th${detailField?.name}`}
                      className={classNames(`span${fieldInfo.columns}`, classes.heading, {
                        [classes.hidden]: !detailField?.visible,
                      })}
                    >
                      <span>{fieldInfo.label}</span>
                    </th>
                  );
                })}
              </tr>
            </thead>
          </>
        );
      }}
    </Observe>
  );
};

const useEditTimecardStyles = makeStyles((_theme) => ({
  activitiesContainer: {
    margin: '12px 0px',
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    marginLeft: 0,
    marginBottom: '4px',
    '&>:first-child': { flex: 1, display: 'flex', alignItems: 'center' },
    '&>:last-child': { marginRight: 0 },
  },
  check: {
    flex: '0 0 auto',
  },
  moreMenu: {
    margin: 0,
  },
  bold: {
    fontWeight: 'bold',
  },
  units: {
    flex: '0 0 auto',
    marginRight: 8,
    display: 'flex',
    '&.equal': {
      color: '#00cc48',
    },
    '&.notequal': {
      color: '#cc000a',
    },
  },
  gridWrapper: {
    padding: '0',
    overflowX: 'auto',
  },

  errorWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: '-6px',
    marginRight: '8px',
    fontSize: '19px',
    color: '#cc000a',
  },

  grid: {
    tableLayout: 'fixed',
    margin: '0',
    maxWidth: 'none',
    borderCollapse: 'separate',
    borderSpacing: 0,
    emptyCells: 'show',
    borderWidth: 0,
    outline: 0,
    minWidth: '100%',
    width: '100%',
    '& .span1': {
      minWidth: '65px',
      width: '65px',
    },
    '& .span2': {
      minWidth: '120px',
      width: '120px',
    },
    '& .span3': {
      minWidth: '160px',
      width: '160px',
    },
    fontSize: '12px',
    '& fieldset': {
      borderColor: 'transparent',
    },
    '& .Mui-disabled fieldset': {
      borderColor: 'transparent !important',
    },
    '&.break .reason': {
      color: '#b9b9b9',
    },
    '&.worked .overtimeReason': {
      color: '#14809d',
    },
    '&.absence .reason': {
      color: '#14809d',
    },
    '&.premium .reason': {
      color: '#9e159e',
    },
    '&.empty': {
      color: '#669639 !important',
      fontStyle: 'italic',
    },
    '&.editing input': {
      fontWeight: 'bold !important',
    },
    borderBottom: '1px solid #eee',
  },
}));

interface IEditTimecardProps {
  model: EditTimecardModel;
  condensedTimecard?: boolean;
  showBreaks?: boolean;
  breakdownMode?: boolean;
  showPremiums?: boolean;
  showUdfs?: boolean;
  moreItems?: IActionMenuItem[];
}

const formatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 4, minimumFractionDigits: 1 });
const CheckBox = editor('checked');

const Toolbar = ({
  model,
  condensedTimecard,
  breakdownMode,
  showBreaks,
  showPremiums,
  showUdfs,
  moreItems,
}: IEditTimecardProps) => {
  const classes = useEditTimecardStyles();
  useWatch(
    () => model.udfEditor.editor?.isOpen,
    () => {
      if (model.udfEditor.editor?.isOpen === false) requestAnimationFrame(() => model.refresh(model.hasChanges));
    },
    false,
  );

  // useWatch(() => { model.udfs; model.udfs.length; }, () => {
  //   model.refresh(model.hasChanges);
  // } , false);

  return (
    <div className={classNames(classes.toolbar)}>
      <div>
        <Observe>
          {() => {
            const current = formatter.format(model.totalCurrentUnits);
            const expected = formatter.format(model.totalExpectedUnits);
            const eq = current === expected;
            const diff = roundTo(model.totalCurrentUnits - model.totalExpectedUnits, 2);
            const title =
              diff < 0
                ? `the current units are less than the expected by ${formatter.format(Math.abs(diff))} hours`
                : `the current units are greater than the expected by ${formatter.format(Math.abs(diff))} hours`;
            return (
              <>
                <span className={classes.units}>{'Current: '}</span>
                <span className={classNames(classes.units, classes.bold, { notequal: !eq, equal: eq })}>{current}</span>
                {eq ? null : (
                  <div className={classes.errorWrapper} title={title}>
                    <ErrorIcon />
                  </div>
                )}
                <span className={classes.units}>{'Expected: '}</span>
                <span className={classNames(classes.units, classes.bold)}>{`${expected}`}</span>
              </>
            );
          }}
        </Observe>
      </div>
      <Observe>
        {() => (
          <div>
            {condensedTimecard ? (
              <CheckBox
                className={classes.check}
                field={{
                  name: 'condensedTimecard',
                  label: 'Condensed View',
                  value: model.condensedTimecard,
                  disabled: () => !model.canSetCondensedTimecard(),
                }}
                onValueChange={(value: boolean) => (model.condensedTimecard = value)}
              />
            ) : null}
            {breakdownMode ? (
              <CheckBox
                className={classes.check}
                field={{
                  name: 'breakdown',
                  label: 'Breakdown Mode',
                  value: model.breakdownMode,
                  disabled: () => !model.canSetBreakdownMode(),
                }}
                onValueChange={(value: boolean) => (model.breakdownMode = value)}
                tooltip="check to split the timecard instead of creating an empty row"
              />
            ) : null}
            {showPremiums ? (
              <CheckBox
                className={classes.check}
                field={{
                  name: 'premiums',
                  label: 'Premiums',
                  value: model.showPremiums,
                  disabled: () => !model.canSetShowPremiums(),
                }}
                onValueChange={(value: boolean) => (model.showPremiums = value)}
              />
            ) : null}
            {showBreaks ? (
              <CheckBox
                className={classes.check}
                field={{ name: 'breaks', label: 'Breaks', value: model.showBreaks, disabled: () => !model.canSetShowBreaks() }}
                onValueChange={(value: boolean) => (model.showBreaks = value)}
              />
            ) : null}
            {showUdfs ? (
              <CheckBox
                className={classes.check}
                field={{ name: 'udfs', label: 'Udfs', value: model.showUdfs, disabled: () => !model.canSetShowUdfs() }}
                onValueChange={(value: boolean) => (model.showUdfs = value)}
              />
            ) : null}
            {moreItems && moreItems.length > 0 ? (
              <OptionsActionMenu
                title="More Options"
                tooltip="More Options"
                classes={{ menuButton: classes.moreMenu }}
                items={moreItems}
              />
            ) : null}
          </div>
        )}
      </Observe>
    </div>
  );
};

const DetailGrid = ({ model }: IEditTimecardProps) => {
  const classes = useEditTimecardStyles();
  return (
    <Observe
      render={() => (
        <div className={classes.gridWrapper}>
          <table className={classes.grid}>
            <HeaderRow model={model} />
            <tbody>
              <Observe>
                {() => (
                  <>
                    {model.detailRows.map((detailRow: IDetailRow, row: number) => (
                      <DetailRow key={detailRow._id} detailRow={detailRow} model={model} row={row} />
                    ))}
                  </>
                )}
              </Observe>
            </tbody>
          </table>
        </div>
      )}
    />
  );
};

const EditTimecardView = (props: IEditTimecardProps) => {
  const model = props.model;
  const classes = useEditTimecardStyles();
  const { navigator, ref } = useKeyboardNavigation();
  const udfEditor = model.udfEditor;
  return (
    <div>
      <div className={classes.activitiesContainer} onKeyDown={navigator.handleKeyDown2} ref={ref} role="application" tabIndex={-1}>
        <Toolbar {...props} />
        <DetailGrid {...props} />
      </div>
      <UdfEditor udfEditor={udfEditor} />
      <AddReasonDialog />
    </div>
  );
};

export default EditTimecardView;
